import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { getDownloadURL, ref } from 'firebase/storage';
import { db, storage } from '../firebase';
import type {
  FirestoreChapterEntry,
  FirestoreChaptersMap,
  FirestoreClip,
  FirestoreGBoxDoc,
  FirestoreGBoxLibraryDoc,
  FirestoreRentalContract,
  FirestoreScene,
  FirestoreAudioSegment,
  RentalLibraryItem,
} from '../types/firestore.types';
import type {
  AudioSegmentItem,
  ChapterInfo,
  ClipItem,
  Duration,
  PlayerItem,
  SceneItem,
} from '../types/player.types';
import { DurationUtils } from '../types/player.types';
import type { TextOverlayData, VectorOverlayData, ArrowOverlayData } from '../types/sceneDesc.types';

// ── Storage URL resolution ───────────────────────────────────────────────────

export async function resolveStoragePath(storagePath: string): Promise<string> {
  if (!storagePath) return '';
  if (storagePath.startsWith('http://') || storagePath.startsWith('https://')) {
    return storagePath;
  }
  // Firebase Storage refs are bucket-relative: a leading slash makes the SDK
  // look up an empty/invalid object. Audio storagePaths written upstream can
  // carry one, which is why they resolve to '' and play as silence. `ref()`
  // already accepts gs:// URLs, so only the leading slash needs normalizing.
  const normalized = storagePath.startsWith('gs://')
    ? storagePath
    : storagePath.replace(/^\/+/, '');
  try {
    return await getDownloadURL(ref(storage, normalized));
  } catch (err) {
    console.warn('[firestoreService] Failed to resolve storage path:', storagePath, err);
    return '';
  }
}

export async function resolveStoragePathsBatch(paths: string[]): Promise<string[]> {
  return Promise.all(paths.map(resolveStoragePath));
}

// ── Content fetching ─────────────────────────────────────────────────────────

export async function fetchGBoxLibraryDoc(
  gboxId: string,
): Promise<FirestoreGBoxLibraryDoc | null> {
  try {
    const snap = await getDoc(doc(db, 'gboxLibrary', gboxId));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() } as FirestoreGBoxLibraryDoc;
  } catch (err) {
    console.warn('[firestoreService] fetchGBoxLibraryDoc failed:', err);
    return null;
  }
}

export async function fetchGBoxContent(gboxId: string): Promise<FirestoreGBoxDoc | null> {
  // 1. Try gboxLibrary to determine which collection to use
  const meta = await fetchGBoxLibraryDoc(gboxId);

  if (meta) {
    const collectionName = meta.gboxAccessType === 'free' ? 'gboxFree' : 'gboxPremium';
    try {
      const snap = await getDoc(doc(db, collectionName, gboxId));
      if (snap.exists()) return snap.data() as FirestoreGBoxDoc;
    } catch (err) {
      console.warn(`[firestoreService] Failed to read ${collectionName}/${gboxId}:`, err);
    }
  }

  // 2. Fallback: try both collections in parallel
  const [freeSnap, premiumSnap] = await Promise.allSettled([
    getDoc(doc(db, 'gboxFree', gboxId)),
    getDoc(doc(db, 'gboxPremium', gboxId)),
  ]);

  if (freeSnap.status === 'fulfilled' && freeSnap.value.exists()) {
    return freeSnap.value.data() as FirestoreGBoxDoc;
  }
  if (premiumSnap.status === 'fulfilled' && premiumSnap.value.exists()) {
    return premiumSnap.value.data() as FirestoreGBoxDoc;
  }

  return null;
}

// ── Transformation: Firestore → PlayerItem ───────────────────────────────────

export async function transformGBoxToPlayerItem(gboxDoc: FirestoreGBoxDoc): Promise<PlayerItem> {
  // Collect ALL storage paths in one pass to batch-resolve them
  const allPaths: string[] = [];
  for (const clip of gboxDoc.clips) {
    for (const scene of clip.scenes) {
      allPaths.push(scene.mediaUrl);
      allPaths.push(scene.thumbnailUrl);
    }
    for (const seg of clip.audioSegments) {
      allPaths.push(seg.segmentMediaUrl);
    }
  }

  const resolvedUrls = await resolveStoragePathsBatch(allPaths);

  // Build O(1) lookup: storagePath → resolvedUrl
  const urlMap = new Map<string, string>();
  allPaths.forEach((path, i) => {
    if (path) urlMap.set(path, resolvedUrls[i]);
  });

  // Parse chapters
  const chapters = parseFirestoreChapters(gboxDoc.chapters);

  // Transform clips
  const clips: ClipItem[] = gboxDoc.clips.map((clip) =>
    transformFirestoreClip(clip, urlMap),
  );

  // Map clip indices into chapters
  for (const chapter of chapters) {
    chapter.clipIndices = chapter.clips
      .map((c) => clips.findIndex((cl) => cl.clipId === c.id))
      .filter((i) => i >= 0);
  }

  const totalDuration = clips.reduce((sum, c) => sum + c.totalDuration, 0);

  return {
    gboxId: gboxDoc.gboxID,
    gboxTitle: gboxDoc.gboxName,
    thumbnailPath: undefined,
    clips,
    chapters,
    totalDuration,
    isOffline: false,
    timedText: gboxDoc.timedText,
  };
}

function parseFirestoreChapters(chaptersMap: FirestoreChaptersMap): ChapterInfo[] {
  const chapters: ChapterInfo[] = [];

  for (const [key, entry] of Object.entries(chaptersMap)) {
    const chapterEntry = entry as FirestoreChapterEntry;
    chapters.push({
      chapterId: key,
      title: chapterEntry.chapterName || `Chapter ${key}`,
      isDefault: chapterEntry.chapterNumber === '0' || key === '0',
      clipIndices: [],
      clips: (chapterEntry.clips ?? []).map((c) => ({ id: c.clipId, name: c.clipName })),
    });
  }

  // Sort numerically by chapterNumber (or key)
  chapters.sort((a, b) => {
    const aNum = parseInt(a.chapterId, 10);
    const bNum = parseInt(b.chapterId, 10);
    return aNum - bNum;
  });

  return chapters;
}

function transformFirestoreClip(
  clip: FirestoreClip,
  urlMap: Map<string, string>,
): ClipItem {
  const scenes = buildSceneTimeline(clip.scenes, urlMap);
  const audioSegments = buildAudioSegments(clip.audioSegments, urlMap);

  const sceneDuration = scenes.reduce((sum, s) => sum + s.duration, 0);
  const audioDuration =
    audioSegments.length > 0
      ? Math.max(...audioSegments.map((a) => a.endTime))
      : 0;
  const clipDurationMs = DurationUtils.fromSeconds(clip.clipDuration ?? 0);
  const totalDuration = Math.max(sceneDuration, audioDuration, clipDurationMs);

  return {
    clipId: clip.clipId,
    clipName: clip.clipName,
    clipDescription: undefined,
    chapterId: clip.chapter?.chapterNumber,
    scenes,
    audioSegments,
    timedText: clip.timedText,
    sceneDuration,
    audioDuration,
    totalDuration,
    latitude: clip.clipLocation?.latitude
      ? parseFloat(clip.clipLocation.latitude)
      : undefined,
    longitude: clip.clipLocation?.longitude
      ? parseFloat(clip.clipLocation.longitude)
      : undefined,
  };
}

function buildSceneTimeline(
  scenes: FirestoreScene[],
  urlMap: Map<string, string>,
): SceneItem[] {
  const result: SceneItem[] = [];
  let currentTime: Duration = 0;

  // Sort by sceneIndex
  const sorted = [...scenes].sort((a, b) => (a.sceneIndex ?? 0) - (b.sceneIndex ?? 0));

  for (const scene of sorted) {
    const duration = DurationUtils.fromSeconds(scene.sceneLength ?? 3);
    const mediaPath = urlMap.get(scene.mediaUrl) ?? '';
    const thumbnailPath = urlMap.get(scene.thumbnailUrl) ?? undefined;
    const mediaType = detectMediaType(mediaPath || scene.mediaUrl);
    const textOverlays = extractTextOverlaysFromCanvas(scene);
    const vectorOverlays = extractVectorOverlaysFromCanvas(scene);
    const sceneDescription = buildSceneDescription(scene);

    result.push({
      sceneId: scene.sceneId,
      sceneName: scene.canvas?.sceneName,
      sceneDescription,
      mediaPath,
      thumbnailPath,
      mediaType,
      duration,
      startTime: currentTime,
      endTime: currentTime + duration,
      textOverlays,
      vectorOverlays,
    });

    currentTime += duration;
  }

  return result;
}

function buildAudioSegments(
  segments: FirestoreAudioSegment[],
  urlMap: Map<string, string>,
): AudioSegmentItem[] {
  const result: AudioSegmentItem[] = [];

  // Sort by segmentIndex
  const sorted = [...segments].sort(
    (a, b) => (a.segmentIndex ?? 0) - (b.segmentIndex ?? 0),
  );

  for (let i = 0; i < sorted.length; i++) {
    const seg = sorted[i];
    const filePath = urlMap.get(seg.segmentMediaUrl) ?? '';
    if (!filePath) {
      // Distinguish "no URL published" from "URL published but failed to
      // resolve" — both end in silence, but only the latter is player-fixable.
      if (seg.segmentMediaUrl) {
        console.warn(
          '[firestoreService] Audio segment skipped — segmentMediaUrl did not resolve:',
          seg.segmentMediaUrl,
        );
      } else {
        console.warn('[firestoreService] Audio segment skipped — no segmentMediaUrl on published segment', seg);
      }
      continue;
    }

    const duration = seg.segmentDuration; // already ms
    const startTime = seg.startTime;      // already ms
    const endTime = startTime + duration;

    result.push({
      id: seg.audioSegmentId || `seg_${i}`,
      audioSegmentId: seg.audioSegmentId || `seg_${i}`,
      filePath,
      mediaType: inferAudioMimeType(filePath || seg.segmentMediaUrl),
      duration,
      startTime,
      endTime,
      trimStart: seg.startTrim || undefined,
      trimEnd: seg.endTrim || undefined,
      volume: 1.0,
      order: seg.segmentIndex,
      spokenLanguageTag: seg.spokenLanguageTag,
      captionsEnabledByDefault: seg.captionsEnabledByDefault !== false,
    });
  }

  return result;
}

function detectMediaType(url: string): 'image' | 'video' {
  const path = url.split('?')[0].toLowerCase();
  const videoExts = ['mp4', 'webm', 'mov', 'avi', 'm4v'];
  const ext = path.split('.').pop() ?? '';
  return videoExts.includes(ext) ? 'video' : 'image';
}

function inferAudioMimeType(url: string): string {
  const path = url.split('?')[0].toLowerCase();
  if (path.endsWith('.m4a') || path.endsWith('.mp4')) return 'audio/mp4';
  if (path.endsWith('.mp3')) return 'audio/mpeg';
  if (path.endsWith('.ogg')) return 'audio/ogg';
  if (path.endsWith('.aac')) return 'audio/aac';
  if (path.endsWith('.webm')) return 'audio/webm';
  return 'audio/mp4'; // default for Firebase Storage audio
}

function extractTextOverlaysFromCanvas(scene: FirestoreScene): TextOverlayData[] {
  // Try canvas.textOverlays first
  const overlays = scene.canvas?.textOverlays ?? scene.textOverlays;
  if (!Array.isArray(overlays) || overlays.length === 0) return [];
  // Cast — they should already match TextOverlayData shape from the editor
  return overlays as TextOverlayData[];
}

function extractVectorOverlaysFromCanvas(scene: FirestoreScene): VectorOverlayData[] {
  const overlays = scene.canvas?.vectorOverlays;
  if (!Array.isArray(overlays) || overlays.length === 0) return [];
  // Normalize legacy arrow `arrowHead` → `endArrowHead` (mirrors the editor's
  // normalizeVectorOverlay so older scenes still render their heads).
  return (overlays as VectorOverlayData[]).map((v) => {
    if (v.type !== 'arrow') return v;
    const arrow = v as ArrowOverlayData;
    if (arrow.endArrowHead === undefined && arrow.arrowHead) {
      return { ...arrow, endArrowHead: arrow.arrowHead, startArrowHead: arrow.startArrowHead ?? 'none' };
    }
    return arrow;
  });
}

// The player's ImageScene/VideoScene derive the canvas height for overlay
// scaling from a JSON `sceneDescription` string (relationships.canvasScene.
// imageRect.height). The published Firestore scene already carries the verbatim
// canvasScene at `scene.canvas`, so synthesize the wrapper the renderers expect.
function buildSceneDescription(scene: FirestoreScene): string | undefined {
  if (!scene.canvas) return undefined;
  return JSON.stringify({ relationships: { canvasScene: scene.canvas } });
}

// ── Rental library ────────────────────────────────────────────────────────────

export async function fetchUserRentalContracts(
  uid: string,
): Promise<FirestoreRentalContract[]> {
  const q = query(
    collection(db, 'rentalContracts'),
    where('uid', '==', uid),
    orderBy('contractStarts', 'desc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as FirestoreRentalContract);
}

export async function fetchRentalContractForUser(
  uid: string,
  gboxId: string,
): Promise<FirestoreRentalContract | null> {
  try {
    const q = query(
      collection(db, 'rentalContracts'),
      where('uid', '==', uid),
      where('gpackId', '==', gboxId),
      orderBy('contractExpires', 'desc'),
      limit(1),
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const d = snap.docs[0];
    return { id: d.id, ...d.data() } as FirestoreRentalContract;
  } catch (err) {
    console.warn('[firestoreService] fetchRentalContractForUser failed:', err);
    return null;
  }
}

export async function buildRentalLibraryItems(
  contracts: FirestoreRentalContract[],
): Promise<RentalLibraryItem[]> {
  const now = new Date();

  // Keep only non-expired contracts
  const active = contracts.filter((c) => {
    try {
      return c.contractExpires.toDate() > now;
    } catch {
      return false;
    }
  });

  if (active.length === 0) return [];

  // Batch-fetch gboxLibrary metadata for each contract
  const metaResults = await Promise.allSettled(
    active.map((c) => fetchGBoxLibraryDoc(c.gpackId)),
  );

  // Resolve cover URLs
  const coverResults = await Promise.allSettled(
    metaResults.map((r) => {
      if (r.status === 'fulfilled' && r.value?.gboxCover) {
        return resolveStoragePath(r.value.gboxCover);
      }
      return Promise.resolve(null);
    }),
  );

  return active.map((contract, i): RentalLibraryItem => {
    const meta =
      metaResults[i].status === 'fulfilled'
        ? (metaResults[i] as PromiseFulfilledResult<FirestoreGBoxLibraryDoc | null>).value
        : null;
    const coverUrl =
      coverResults[i].status === 'fulfilled'
        ? (coverResults[i] as PromiseFulfilledResult<string | null>).value
        : null;

    const expires = contract.contractExpires.toDate();
    const daysRemaining = Math.ceil((expires.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    return {
      gboxId: contract.gpackId,
      gboxName: meta?.gboxName ?? 'Guide',
      gboxDescription: meta?.gboxDescription ?? '',
      gboxAccessType: meta?.gboxAccessType ?? 'free',
      coverUrl: coverUrl ?? null,
      contractId: contract.id,
      contractExpires: expires,
      daysRemaining,
      isActive: contract.isActive,
      permissionSource: contract.permissionSource,
    };
  });
}
